import { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  spaceUrl: string;
  isSpaceCreator: boolean;
}

export function WelcomeModal({ isOpen, onClose, spaceUrl, isSpaceCreator }: WelcomeModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(spaceUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleClose = () => {
    localStorage.setItem('welcomeModalSeen', 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-[1px]">
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 p-6 pb-8">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <h2 className="text-2xl font-bold text-white mb-2">
            Welcome to Your Space! 🚀
          </h2>
          <p className="text-blue-100 text-sm">
            Share files instantly with peer-to-peer encryption
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* How it works */}
          <div>
          <h3 className="text-lg font-semibold text-white mb-3">
              How it works:
            </h3>
            <ol className="space-y-2 text-gray-300 text-sm">
              {isSpaceCreator ? (
                <>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                      1
                    </span>
                    <span>
                      <strong className="text-white">Share your space URL</strong> with your partner using the button below
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                      2
                    </span>
                    <span>
                      Once they join, <strong className="text-white">click on their circle</strong> to select and send a file
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                      3
                    </span>
                    <span>
                      Files are transferred <strong className="text-white">directly and securely</strong> — no cloud storage!
                    </span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                      1
                    </span>
                    <span>
                      You've joined a <strong className="text-white">shared space</strong> — wait for your partner to connect
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                      2
                    </span>
                    <span>
                      Once connected, <strong className="text-white">click on their circle</strong> to select and send a file
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold mr-3 mt-0.5 flex-shrink-0">
                      3
                    </span>
                    <span>
                      Files are transferred <strong className="text-white">directly and securely</strong> — no cloud storage!
                    </span>
                  </li>
                </>
              )}
            </ol>
          </div>

          {/* Copy Link Button - Only show for space creators */}
          {isSpaceCreator && (
            <div>
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all shadow-lg"
              >
                {copied ? (
                  <>
                    <Check size={20} />
                    <span>Link Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy size={20} />
                    <span>Copy Space Link</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Pricing Info */}
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-lg p-4">
            <h4 className="text-white font-semibold mb-2 text-sm">
              💎 Upgrade for More
            </h4>
            <p className="text-gray-300 text-xs leading-relaxed">
              <strong>Free:</strong> Up to 10 MB per file
              <br />
              <strong>Premium:</strong> Just $1 one-time payment to unlock unlimited file size & unlimited transfers!
            </p>
          </div>

          {/* Get Started Button */}
          <button
            onClick={handleClose}
            className="w-full px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-all"
          >
            Get Started
          </button>
        </div>
      </div>
    </div>
  );
}
